<template>
  <b-card class="border mt-1 mb-1">
    <div v-if="this.banner.src">
      <Cropper ref="cropper" class="cropper-banner" :src="banner.src" stencil-component="rectangle-stencil"
               :stencil-props="{ aspectRatio: 5/1 }"></Cropper>
      <div class="btn-group d-flex mt-2" role="group">
        <div class="btn btn-secondary btn-sm" @click="resetCropper">Cancel</div>
        <div class="btn btn-warning btn-sm" @click="rotate">Rotate</div>
        <div class="btn btn-primary btn-sm" @click="onProjectBannerSelected">Crop</div>
        <BannerGuidelinesButton button-id="banner-info-cropper" button-class="btn btn-outline-info btn-sm ml-3 rounded-0" />
      </div>
    </div>

    <div v-else class="row justify-content-center mb-2">
      <i v-if="isLoading" class="fa fa-3x fa-spin fa-spinner ml-1 mt-2"></i>
      <div v-else class="profile-background p-4 mb-2">
        <b-img thumbnail fluid alt="Project Banner" :src="projectBanner"/>
      </div>
      <div class="col-12">
        <div class="row justify-content-center">
          <div class="d-flex align-items-center">
            <VueUploadComponent
              v-if="!bannerPresent"
              v-bind="fileUploadOptions"
              v-model='fileProjectBanner'
              @input-filter="inputFilter"
              ref="uploadBanner"
              input-id="banner-upload-input"
              name="banner-upload"
              alt="Add a project banner"
              title="Add a project banner"
              class="btn btn-primary mb-1"
            >
              <span><i class="fa fa-plus" aria-hidden="true" title="Add a project banner"></i> <b>Add Banner</b></span>
            </VueUploadComponent>
            <div v-else class="d-flex">
              <VueUploadComponent
                v-bind="fileUploadOptions"
                v-model='fileProjectBanner'
                @input-filter="inputFilter"
                ref="uploadBanner"
                input-id="banner-upload-input"
                name="banner-upload"
                alt="Change project banner"
                title="Change project banner"
                class='btn btn-secondary mb-1'
              >
                <span><i class="fa fa-pencil" aria-hidden="true" title="Change project banner"></i> Change</span>
              </VueUploadComponent>
              <b-btn class="ml-1 mb-1" variant="danger" size="md" title="Remove project banner"
                     @click="removeProjectBanner">
                <span><i class="fa fa-trash" aria-hidden="true" title="Remove project banner"></i> Remove</span>
              </b-btn>
            </div>
            <BannerGuidelinesButton button-id="banner-info-upload" button-class="btn btn-outline-info btn-md ml-2 mb-1" />
          </div>
        </div>
      </div>
      <div class='text-danger' v-if='fileTypeIssue'>Project banner must be a .jpg, .jpeg, or .png</div>
      <div class='text-danger' v-if='fileSizeIssue'>Project banner must be less than 10MB</div>
    </div>
  </b-card>
</template>

<script>

import {mapGetters, mapActions} from 'vuex'
import VueUploadComponent from 'vue-upload-component'
import {Cropper} from 'vue-advanced-cropper';
import 'vue-advanced-cropper/dist/style.css';
import BannerGuidelinesButton from '../../../common/BannerGuidelinesButton.vue';

export default {
  name: 'EditProjectBanner',
  components: {
    VueUploadComponent,
    Cropper,
    BannerGuidelinesButton,
  },
  data() {
    return {
      rounded: false,
      fileUploadOptions: {
        multiple: false,
        drop: true,
        accept: this.projectBannerAccept,
        extensions: this.projectBannerExtensions,
        size: 1024 * 1024 * 10,
        valueDeterminate: 100,
        imageUploadedSuccessfully: false,
        showUploadedImage: 0
      },
      fileProjectBanner: [],
      fileTypeIssue: false,
      fileSizeIssue: false,
      banner: {
        src: null,
        type: null
      },
      isLoading: false,
    }
  },
  created() {
  },

  watch: {
    fileProjectBanner(newVal, oldVal) {
      if (newVal) {
        // If a file is uploaded, put it through the cropper
        if (newVal[0]) {
          if (this.banner.src) {
            URL.revokeObjectURL(this.banner.src)
          }
          // 2. Create the blob link to the file to optimize performance:
          const blob = URL.createObjectURL(newVal[0].file);

          // Create a new FileReader to read this image binary data
          const reader = new FileReader();
          // Define a callback function to run, when FileReader finishes its job
          reader.onload = (e) => {
            this.banner = {
              // Set the image source
              src: blob,
              // Determine the image type
              type: newVal[0].type,
            };
          };
          // Start the reader job - read file as a data url (base64 format)
          reader.readAsArrayBuffer(newVal[0].file);
        }
      }
    },
    uploadBannerStatus(newVal) {
      // Only react to banner (project_banner) uploads, not logo uploads
      if (newVal && newVal.type === this.projectBannerType) {
        this.resetCropper()
        let currentStatus = newVal.status
        if (currentStatus === 'SUCCESS') {
          this.isLoading = false
        } else {
          this.isLoading = currentStatus === 'IS_UPLOADING';
          if (!this.isLoading) {
            // Handle errors here
          }
        }
      }
    },
  },
  computed: {
    ...mapGetters({
      projectVuex: 'project/get/project',
      projectBannerType: 'constants/projectBannerType',
      projectBannerAccept: 'constants/projectBannerAccept',
      projectBannerExtensions: 'constants/projectBannerExtensions',
      uploadBannerStatus: "project/images/upload",
    }),
    projectBanner() {
      return (this.projectVuex && this.projectVuex.bannerPicture && this.projectVuex.bannerPicture.path)
        ? this.projectVuex.bannerPicture.path
        : '/assets/images/header/default-project-header-banner.png';
    },
    bannerPresent() {
      return this.projectBanner !== '/assets/images/header/default-project-header-banner.png'
    },
  },
  methods: {
    ...mapActions({
      uploadBanner: "project/images/uploadImage",
      updateProject: "project/update/updateProject",
    }),
    inputFilter(newFile, oldFile, prevent) {
      if (newFile && !oldFile) {
        let fileTypeIssue = false;
        let fileSizeIssue = false;
        if (!/\.(jpg|jpeg|png|webp)$/i.test(newFile.name)) {
          fileTypeIssue = true;
        }
        if (newFile.size > 10 * 1000000) {
          fileSizeIssue = true;

        }
        if (fileTypeIssue || fileSizeIssue) {
          this.fileTypeIssue = (fileTypeIssue || this.fileTypeIssue);
          this.fileSizeIssue = (fileSizeIssue || this.fileSizeIssue);
          return prevent()
        }
      }
      if (newFile && (!oldFile || newFile.file !== oldFile.file)) {
      }
    },
    onProjectBannerSelected() {
      const {canvas} = this.$refs.cropper.getResult();
      if (canvas) {
        canvas.toBlob(blob => {
          this.uploadBanner({
            blob: blob,
            name: this.fileProjectBanner[0].name,
            type: this.projectBannerType,
          })
        });
      }
    },
    rotate() {
      this.$refs.cropper.rotate(90);
    },
    resetCropper() {
      this.banner.src = null
      this.banner.type = null
    },
    removeProjectBanner() {
      this.updateProject({project: {bannerPicture: null}})
    },
  },
}
</script>

<style scoped lang="scss">

.profile-background {
  background-color: #C0C0C0;
}

.cropper-banner {
  max-height: 5000px;
  max-width: 1100px;
  background-color: #DDD;
}
</style>
